import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { isOnline } from "@/lib/presence";
import { Loader2, MessageCircle, UserPlus2 } from "lucide-react";
import Avatar from "@/components/Avatar";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Messages() {
  const { t } = useLang();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get("/messages/conversations"),
        api.get("/connections?status=pending_incoming"),
      ]);
      setConvs(cRes.data);
      setPending(pRes.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const respond = async (c, accept) => {
    setBusyId(c.id);
    try {
      await api.post(`/connections/${c.id}/respond`, { accept });
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="messages-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("messages")}</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">{t("messages")}</h1>
      </div>

      {pending.length > 0 && (
        <div className="mb-6" data-testid="pending-section">
          <div className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] font-bold mb-2">
            {t("connectionRequest")} ({pending.length})
          </div>
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-2xl bg-[hsl(var(--elevated))] border border-[hsl(var(--primary))]/30" data-testid={`pending-req-${c.id}`}>
                <Avatar user={c.peer} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.peer.alias}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{c.peer.city || "—"} · {t("wantsToConnect")}</div>
                </div>
                <button
                  onClick={() => respond(c, true)}
                  disabled={busyId === c.id}
                  data-testid={`pending-accept-${c.id}`}
                  className="px-3 py-1.5 rounded-full text-sm font-medium bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary-hover))] disabled:opacity-50"
                >
                  {t("accept")}
                </button>
                <button
                  onClick={() => respond(c, false)}
                  disabled={busyId === c.id}
                  data-testid={`pending-decline-${c.id}`}
                  className="px-3 py-1.5 rounded-full text-sm font-medium border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-white disabled:opacity-50"
                >
                  {t("decline")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : convs.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-3xl p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="msgs-empty">
          <MessageCircle className="h-8 w-8 mx-auto text-[hsl(var(--primary))] mb-3" />
          <p className="max-w-sm mx-auto text-sm">{t("noConnections")}</p>
          <button
            onClick={() => nav("/discover")}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white text-sm font-medium"
            data-testid="msgs-go-discover"
          >
            <UserPlus2 className="h-4 w-4" /> {t("discover")}
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl overflow-hidden divide-y divide-[hsl(var(--border))]" data-testid="conversation-list">
          {convs.map((c) => (
            <button
              key={c.peer.id}
              onClick={() => nav(`/messages/${c.peer.id}`)}
              data-testid={`conv-${c.peer.id}`}
              className="w-full flex items-center gap-3 p-4 text-start hover:bg-[hsl(var(--elevated))] transition-colors"
            >
              <div className="relative shrink-0">
                <Avatar user={c.peer} size={48} />
                {isOnline(c.peer.last_seen_at) && (
                  <span
                    className="absolute bottom-0 end-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white"
                    data-testid={`conv-online-${c.peer.id}`}
                    aria-label="online"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{c.peer.alias}</span>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))] shrink-0">{timeAgo(c.last_time)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  {c.peer.peer_typing ? (
                    <span className="text-sm truncate text-[hsl(var(--primary))] font-medium italic" data-testid={`conv-typing-${c.peer.id}`}>
                      {t("typing")}
                    </span>
                  ) : (
                    <span className={`text-sm truncate ${c.unread > 0 ? "text-[hsl(var(--foreground))] font-medium" : "text-[hsl(var(--muted-foreground))]"}`}>
                      {c.last_text || <span className="italic">{t("typeMessage")}</span>}
                    </span>
                  )}
                  {c.unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold" data-testid={`conv-unread-${c.peer.id}`}>
                      {c.unread > 9 ? "9+" : c.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
